import { useTranslation } from "react-i18next";
import { HeartPulse, Leaf, LayoutGrid, Sparkles } from "lucide-react";

import { brand, brandAssetPath } from "@shared/config";
import { Card, CardContent } from "@shared/ui/card";

import { useEcosystemCategories } from "../ecosystem-section/featureCategories";

const visualImages = [
  "assets/diet-protocols/autoimmune-protocol.webp",
  "assets/diet-protocols/carnivore.webp",
  "assets/diet-protocols/ketogenic.webp",
] as const;

export function HeroVisual() {
  const { t } = useTranslation("landing");
  const assetBase = import.meta.env.BASE_URL;
  const { featureCount } = useEcosystemCategories();
  void brand;

  return (
    <div className="relative mx-auto min-h-[28rem] w-full max-w-xl lg:min-h-[36rem]">
      <div
        className="absolute start-8 top-8 h-24 w-24 rounded-full bg-primary/15 blur-2xl"
        aria-hidden="true"
      />
      <div
        className="absolute end-8 top-24 h-32 w-32 rounded-full bg-chart-2/25 blur-3xl"
        aria-hidden="true"
      />

      <div className="landing-float-slow absolute start-3 top-20 z-10 rounded-2xl border border-border bg-card/90 p-3 shadow-xl backdrop-blur sm:start-10">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <HeartPulse className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {t("hero.visual.cardOneLabel")}
            </p>
            <p className="text-sm font-semibold text-foreground">
              {t("hero.visual.cardOneValue")}
            </p>
          </div>
        </div>
      </div>

      <Card className="landing-float relative z-20 mx-auto mt-12 max-w-md overflow-hidden rounded-2xl border-2 bg-card shadow-2xl">
        <CardContent className="p-0">
          <div className="border-b border-border bg-muted/50 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  {t("hero.visual.dashboardEyebrow")}
                </p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {t("hero.visual.dashboardTitle")}
                </p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Leaf className="h-6 w-6" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="space-y-5 p-5">
            <div className="grid grid-cols-3 gap-3">
              {visualImages.map((src, index) => (
                <img
                  key={src}
                  src={brandAssetPath(assetBase, src)}
                  alt={t(`hero.visual.imageAlt${index + 1}`)}
                  width={160}
                  height={160}
                  loading={index === 0 ? "eager" : "lazy"}
                  className="aspect-square rounded-2xl border border-border bg-muted object-cover shadow-sm"
                />
              ))}
            </div>

            <div className="rounded-2xl border border-border bg-background p-4">
              <div className="mb-3 flex items-center justify-between gap-4">
                <p className="text-sm font-semibold text-foreground">
                  {t("hero.visual.patternTitle")}
                </p>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {t("hero.visual.patternStatus")}
                </span>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded-full bg-muted">
                  <div className="h-3 w-3/4 rounded-full bg-primary" />
                </div>
                <div className="h-3 w-full rounded-full bg-muted">
                  <div className="h-3 w-1/2 rounded-full bg-chart-2" />
                </div>
                <div className="h-3 w-full rounded-full bg-muted">
                  <div className="h-3 w-5/6 rounded-full bg-chart-3" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="landing-float-slower absolute end-0 bottom-12 z-30 max-w-[16rem] rounded-2xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              {t("hero.visual.cardTwoLabel")}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("hero.visual.cardTwoValue")}
            </p>
          </div>
        </div>
      </div>

      <div className="landing-float-slow absolute start-0 bottom-2 z-30 flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-2 text-xs font-semibold text-primary shadow-lg backdrop-blur sm:bottom-4 sm:start-4">
        <LayoutGrid className="h-4 w-4" aria-hidden="true" />
        {t("hero.visual.featureCountLabel", { count: featureCount })}
      </div>
    </div>
  );
}

export default HeroVisual;
